// To see what to track: https://docs.google.com/document/d/1vqI8ffNHczh3FX_xYfUE-0AFbrcvz70uI74dk539w-I/edit?tab=t.0

import type { MouseEventHandler } from 'react';
import type { ModelType, TrimType } from '../types';

type TrackingValue<T = string> = T | null | undefined;

type VehicleDetails = {
  vehicle_nameplate: TrackingValue;
  vehicle_type: TrackingValue;
};

type VehicleConfiguration = VehicleDetails & {
  vehicle_year: TrackingValue;
  vehicle_trim: TrackingValue;
};

export type TrackingEvent =
  | {
      event: 'newsletter_signup';
    }
  | (VehicleDetails & {
      event: 'change_model_company_car';
      lease_type: TrackingValue<'finance' | 'business'>;
    })
  | (VehicleDetails & {
      event: 'contact_dealer';
    })
  | (VehicleConfiguration & {
      event: 'find_a_dealer';
      form_step: TrackingValue<'contact dealer' | 'visit website'>;
      zip_code: TrackingValue;
      dealer_id: TrackingValue;
      dealer_name: TrackingValue;
    })
  | (VehicleConfiguration & {
      event: 'request_brochure';
      form_step: TrackingValue<'download'>;
      document_type_list: TrackingValue;
    });

type TrackingInput =
  | Exclude<TrackingEvent, { event: 'contact_dealer' | 'find_a_dealer' }>
  | {
      event: 'contact_dealer';
      model: Pick<ModelType, 'name' | 'type'> | null | undefined;
    }
  | {
      event: 'find_a_dealer';
      model: Pick<ModelType, 'name' | 'type'> | null | undefined;
      trim?: Pick<TrimType, 'name'> | null;
      form_step?: TrackingValue<'contact dealer' | 'visit website'>;
      zip_code?: TrackingValue;
      dealer_id?: TrackingValue;
      dealer_name?: TrackingValue;
    };

/** Only infer events for known destinations, never from link text or query values. */
export const resolveLinkTrackingEvent = (
  href: string,
  baseUrl: string,
): TrackingInput | undefined => {
  if (!href.trim() || href.trim().startsWith('#')) return;

  try {
    const url = new URL(href, baseUrl);
    if (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      (url.hostname === 'www.kia.com' || url.hostname === 'kia.com')
    ) {
      switch (url.pathname.replace(/\/$/, '')) {
        case '/se/sok-aterforsaljare-verkstad':
          return { event: 'find_a_dealer', model: undefined };
        case '/se/provkor':
          return { event: 'contact_dealer', model: undefined };
      }
    }
  } catch {
    // Invalid CMS URLs should not break interaction with the page.
  }
};

/** Opt in on anchors rendered from structured pagebuilder link fields only. */
export const trackLinkClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
  const input = resolveLinkTrackingEvent(
    event.currentTarget.getAttribute('href') ?? '',
    document.baseURI,
  );
  if (input) trackEvent(input);
};

export const trackEvent = (input: TrackingInput) => {
  if (typeof window !== 'undefined') {
    const event: TrackingEvent =
      input.event === 'contact_dealer'
        ? {
            event: input.event,
            vehicle_nameplate: input.model?.name,
            vehicle_type: input.model?.type?.name,
          }
        : input.event === 'find_a_dealer'
          ? {
              event: input.event,
              vehicle_nameplate: input.model?.name,
              vehicle_type: input.model?.type?.name,
              vehicle_year: undefined,
              vehicle_trim: input.trim?.name,
              form_step: input.form_step ?? 'contact dealer',
              zip_code: input.zip_code,
              dealer_id: input.dealer_id,
              dealer_name: input.dealer_name,
            }
          : input;

    window.dataLayer = window.dataLayer || [];
    const payload = Object.fromEntries(
      Object.entries(event).map(([key, value]) => [key, value ?? undefined]),
    );
    window.dataLayer.push(payload);
  }
};
